import React from 'react';
import { Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, IconButton, Popper, Paper, MenuList, MenuItem, ClickAwayListener } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAllProjects } from '../../services/projectService';
import { offerStandaloneService, OfferStandalone } from '../../services/offerStandaloneService';

export interface OverlaySidebarProps {
  open: boolean;
  onClose: () => void;
}

const menu = [
  { key: 'home', text: 'Strona główna', icon: '/assets/sidebar/home.svg', path: '/' },
  { key: 'projects', text: 'Projekty', icon: '/assets/sidebar/projects.svg', path: '/projects' },
  { key: 'offers', text: 'Oferty', icon: '/assets/sidebar/offers.svg', path: '/offers' },
  { key: 'database', text: 'Bazy danych', icon: '/assets/sidebar/database.svg', path: '/database' },
  { key: 'apps', text: 'Aplikacje', icon: '/assets/sidebar/apps.svg', path: '/app' },
  { key: 'invoices', text: 'Faktury', icon: '/assets/sidebar/invoices.svg', path: '/accounting/purchases' },
  { key: 'service', text: 'Serwis', icon: '/assets/sidebar/service.svg', path: '/service' },
  { key: 'bug', text: 'Zgłoś błąd', icon: '/assets/sidebar/bug.svg', path: '/bug-report' },
  { key: 'permissions', text: 'Ustawienia', icon: '/assets/sidebar/settings.svg', path: '/settings' },
  { key: 'help', text: 'Pomoc', icon: '/assets/sidebar/help.png', path: '/help' },
];

const DRAWER_WIDTH = 300;

const OverlaySidebar: React.FC<OverlaySidebarProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [highlighted, setHighlighted] = React.useState<number>(0);
  const [submenuOpen, setSubmenuOpen] = React.useState<boolean>(false);
  const [submenuIndex, setSubmenuIndex] = React.useState<number>(0);
  const [submenuAnchorEl, setSubmenuAnchorEl] = React.useState<HTMLElement | null>(null);
  const [submenuFor, setSubmenuFor] = React.useState<'projects'|'apps'|'offers'|null>(null);
  const [inProgressProjects, setInProgressProjects] = React.useState<Array<{ id: string; label: string }>>([]);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [loadingProjects, setLoadingProjects] = React.useState<boolean>(false);
  const [projectsExpanded, setProjectsExpanded] = React.useState<boolean>(false);
  const [projectsTreeIndex, setProjectsTreeIndex] = React.useState<number>(0);
  const [projectCounts, setProjectCounts] = React.useState<{ inProgress: number; warranty: number; afterWarranty: number }>({ inProgress: 0, warranty: 0, afterWarranty: 0 });
  const [projectsListAnchorEl, setProjectsListAnchorEl] = React.useState<HTMLElement | null>(null);
  const [offersExpanded, setOffersExpanded] = React.useState<boolean>(false);
  const [offersTreeIndex, setOffersTreeIndex] = React.useState<number>(0);
  const [offerCounts, setOfferCounts] = React.useState<{ offered: number; prequalification: number; finished: number; archive: number }>({ offered: 0, prequalification: 0, finished: 0, archive: 0 });
  const [offersListAnchorEl, setOffersListAnchorEl] = React.useState<HTMLElement | null>(null);
  const [currentOffers, setCurrentOffers] = React.useState<Array<{ id: string; label: string }>>([]);
  const [loadingOffers, setLoadingOffers] = React.useState<boolean>(false);

  const can = (key: string) => {
    if (user?.role && String(user.role).toLowerCase() === 'admin') return true;
    if (!user?.permissions || Object.keys(user.permissions).length === 0) return true;
    if (key === 'invoices' && !(key in user.permissions)) return true;
    if (key === 'apps') {
      const appKeys = ['estimates','materials_approvals','schedules','maps','requester','human_resources'];
      return appKeys.some(k => !!user.permissions?.[k as keyof typeof user.permissions]) || appKeys.some(k => !(k in (user.permissions || {})));
    }
    return !!user.permissions[key as keyof typeof user.permissions];
  };

  const items = React.useMemo(() => menu.filter(m => can(m.key)), [user]);

  const loadProjects = React.useCallback(async () => {
    try {
      setLoadingProjects(true);
      const projects: any[] = await getAllProjects();
      
      const inProgress = (projects || [])
        .filter((p: any) => (String(p.project_phase || '').trim().toUpperCase()) === 'W REALIZACJI')
        .sort((a: any, b: any) => {
          const pa = parseInt(String(a.project_id || '').split('-')[0]);
          const pb = parseInt(String(b.project_id || '').split('-')[0]);
          if (Number.isNaN(pa) || Number.isNaN(pb)) return 0;
          return pb - pa;
        })
        .map((p: any) => ({ id: p.project_id, label: `${p.project_id} ${p.project_name || ''}`.trim() }));
      
      const warranty = (projects || [])
        .filter((p: any) => (String(p.project_phase || '').trim().toUpperCase()) === 'ZAKOŃCZONE W OKRESIE GWARANCJI');
      
      const afterWarranty = (projects || [])
        .filter((p: any) => (String(p.project_phase || '').trim().toUpperCase()) === 'ZAKOŃCZONE PO GWARANCJI');
      
      setInProgressProjects(inProgress);
      setProjectCounts({
        inProgress: inProgress.length,
        warranty: warranty.length,
        afterWarranty: afterWarranty.length
      });
    } catch (e) {
      console.error('Nie udało się pobrać listy projektów', e);
      setInProgressProjects([]);
      setProjectCounts({ inProgress: 0, warranty: 0, afterWarranty: 0 });
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  const loadOffers = React.useCallback(async () => {
    try {
      setLoadingOffers(true);
      const offers: OfferStandalone[] = await offerStandaloneService.getAllOffers();
      
      const offered = (offers || [])
        .filter((o: OfferStandalone) => ['w trakcie', 'najlepsza oferta', 'druga oferta', 'trzecia i dalej oferta', 'Uzupełnianie dokumentów'].includes(o.status))
        .sort((a: OfferStandalone, b: OfferStandalone) => {
          const dateA = a.submission_deadline ? new Date(a.submission_deadline).getTime() : 0;
          const dateB = b.submission_deadline ? new Date(b.submission_deadline).getTime() : 0;
          return dateA - dateB;
        })
        .map((o: OfferStandalone) => ({ 
          id: o.id, 
          label: o.offer_sequence_number 
            ? `OF.${o.offer_sequence_number}.${o.created_at ? o.created_at.substring(0, 10) : ''} ${o.order_name || ''}`.trim()
            : `${o.id.substring(0, 8)} ${o.order_name || ''}`.trim()
        }));
      
      const prequalification = (offers || [])
        .filter((o: OfferStandalone) => {
          if (o.status !== 'Prekwalifikacja') return false;
          const tso = o.submission_deadline ? new Date(o.submission_deadline).getTime() : 0;
          const today = new Date().setHours(0, 0, 0, 0);
          return tso >= today;
        });
      
      const finished = (offers || [])
        .filter((o: OfferStandalone) => ['Wygrana', 'odrzucona', 'przegrana'].includes(o.status));
      
      const archive = (offers || [])
        .filter((o: OfferStandalone) => {
          if (o.status !== 'Prekwalifikacja') return false;
          const tso = o.submission_deadline ? new Date(o.submission_deadline).getTime() : 0;
          const today = new Date().setHours(0, 0, 0, 0);
          return tso < today;
        });
      
      setCurrentOffers(offered);
      setOfferCounts({
        offered: offered.length,
        prequalification: prequalification.length,
        finished: finished.length,
        archive: archive.length
      });
    } catch (e) {
      console.error('Nie udało się pobrać listy ofert', e);
      setCurrentOffers([]);
      setOfferCounts({ offered: 0, prequalification: 0, finished: 0, archive: 0 });
    } finally {
      setLoadingOffers(false);
    }
  }, []);

  // Ustaw początkowe zaznaczenie po otwarciu: bieżąca ścieżka lub pierwszy element
  React.useEffect(() => {
    if (open) {
      const idx = Math.max(0, items.findIndex(i => i.path === location.pathname || (i.key === 'invoices' && location.pathname.startsWith('/accounting'))));
      setHighlighted(idx >= 0 ? idx : 0);
      setSubmenuOpen(false);
      setSubmenuAnchorEl(null);
      setProjectsExpanded(false);
      setProjectsListAnchorEl(null);
      setProjectsTreeIndex(0);
      // Spróbuj od razu zafokusować kontener, aby strzałki działały pewnie
      setTimeout(() => containerRef.current?.focus(), 0);
    }
  }, [open, location.pathname, items]);

  // Załaduj projekty przy otwarciu menu projektów
  React.useEffect(() => {
    if (open && submenuFor === 'projects' && projectCounts.inProgress === 0 && projectCounts.warranty === 0 && projectCounts.afterWarranty === 0 && !loadingProjects) {
      loadProjects();
    }
  }, [open, submenuFor, projectCounts, loadingProjects, loadProjects]);

  // Jeśli trasa się zmieni, a overlay jest otwarty → zamknij (zapobiega pozostawaniu submenu po nawigacji)
  React.useEffect(() => {
    if (open) {
      setSubmenuOpen(false);
      setSubmenuAnchorEl(null);
      // delikatnie po klatce, aby nie kolidować z przejściem trasy
      const t = setTimeout(() => onClose(), 0);
      return () => clearTimeout(t);
    }
  }, [location.pathname]);

  // Wspólna obsługa klawiszy (używana przez fokusowany kontener)
  const handleKeys = (e: { key: string; preventDefault: () => void; target: any }) => {
      const editableTag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      const isEditable = editableTag === 'input' || editableTag === 'textarea' || (e.target as any)?.isContentEditable;
      if (isEditable) return;

      const closeSubmenu = () => {
        setSubmenuOpen(false);
        setSubmenuAnchorEl(null);
        setSubmenuFor(null);
        setProjectsExpanded(false);
        setProjectsListAnchorEl(null);
        setOffersExpanded(false);
        setOffersListAnchorEl(null);
        // przywróć fokus do głównego kontenera, aby strzałki działały
        setTimeout(() => containerRef.current?.focus(), 0);
      };

      if (submenuOpen) {
        if (e.key === 'ArrowLeft' || e.key === 'Escape') {
          e.preventDefault();
          if (submenuFor === 'projects' && projectsExpanded) {
            setProjectsExpanded(false);
            setProjectsListAnchorEl(null);
            setProjectsTreeIndex(0);
            return;
          }
          if (submenuFor === 'offers' && offersExpanded) {
            setOffersExpanded(false);
            setOffersListAnchorEl(null);
            setOffersTreeIndex(0);
            return;
          }
          closeSubmenu();
          return;
        }
        if (submenuFor === 'projects') {
          if (projectsExpanded) {
            // W rozwiniętej liście projektów - obsługa strzałek dla listy projektów
            if (e.key === 'ArrowDown' || e.key === 'PageDown') {
              e.preventDefault();
              setSubmenuIndex(prev => Math.min(prev + 1, inProgressProjects.length - 1));
              return;
            } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
              e.preventDefault();
              setSubmenuIndex(prev => Math.max(prev - 1, 0));
              return;
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const project = inProgressProjects[submenuIndex];
              if (project) {
                navigate(`/projects/${encodeURIComponent(project.id)}`);
                closeSubmenu();
                setTimeout(() => onClose(), 0);
              }
              return;
            }
          } else {
            // Menu drzewa projektów - obsługa strzałek
            if (e.key === 'ArrowDown' || e.key === 'PageDown') {
              e.preventDefault();
              setProjectsTreeIndex(prev => Math.min(prev + 1, 2));
              return;
            } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
              e.preventDefault();
              setProjectsTreeIndex(prev => Math.max(prev - 1, 0));
              return;
            } else if (e.key === 'Enter' || e.key === 'ArrowRight') {
              e.preventDefault();
              if (projectsTreeIndex === 0) {
                // Rozwiń "W realizacji"
                const anchorEl = document.getElementById('projects-in-progress-item');
                setProjectsExpanded(true);
                setProjectsListAnchorEl(anchorEl);
                setSubmenuIndex(0);
              } else if (projectsTreeIndex === 1) {
                // Przejdź do "Zakończone w okresie gwarancji"
                navigate('/projects?tab=1');
                closeSubmenu();
                setTimeout(() => onClose(), 0);
              } else if (projectsTreeIndex === 2) {
                // Przejdź do "Zakończone po gwarancji"
                navigate('/projects?tab=2');
                closeSubmenu();
                setTimeout(() => onClose(), 0);
              }
              return;
            }
          }
        }
        if (submenuFor === 'offers') {
          if (offersExpanded) {
            // W rozwiniętej liście ofert - obsługa strzałek dla listy ofert
            if (e.key === 'ArrowDown' || e.key === 'PageDown') {
              e.preventDefault();
              setSubmenuIndex(prev => Math.min(prev + 1, currentOffers.length - 1));
              return;
            } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
              e.preventDefault();
              setSubmenuIndex(prev => Math.max(prev - 1, 0));
              return;
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const offer = currentOffers[submenuIndex];
              if (offer) {
                navigate(`/offers/${encodeURIComponent(offer.id)}`);
                closeSubmenu();
                setTimeout(() => onClose(), 0);
              }
              return;
            }
          } else {
            // Menu drzewa ofert - obsługa strzałek
            if (e.key === 'ArrowDown' || e.key === 'PageDown') {
              e.preventDefault();
              setOffersTreeIndex(prev => Math.min(prev + 1, 3));
              return;
            } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
              e.preventDefault();
              setOffersTreeIndex(prev => Math.max(prev - 1, 0));
              return;
            } else if (e.key === 'Enter' || e.key === 'ArrowRight') {
              e.preventDefault();
              if (offersTreeIndex === 0) {
                // Rozwiń "Aktualnie ofertowane"
                const anchorEl = document.getElementById('offers-current-item');
                setOffersExpanded(true);
                setOffersListAnchorEl(anchorEl);
                setSubmenuIndex(0);
              } else if (offersTreeIndex === 1) {
                // Przejdź do "Prekwalifikacja"
                navigate('/offers?tab=1');
                closeSubmenu();
                setTimeout(() => onClose(), 0);
              } else if (offersTreeIndex === 2) {
                // Przejdź do "Zakończone"
                navigate('/offers?tab=2');
                closeSubmenu();
                setTimeout(() => onClose(), 0);
              } else if (offersTreeIndex === 3) {
                // Przejdź do "Archiwum prekwalifikowanych"
                navigate('/offers?tab=3');
                closeSubmenu();
                setTimeout(() => onClose(), 0);
              }
              return;
            }
          }
        }
        if (submenuFor === 'apps') {
          // Menu aplikacji - obsługa strzałek
          if (e.key === 'ArrowDown' || e.key === 'PageDown') {
            e.preventDefault();
            setSubmenuIndex(prev => Math.min(prev + 1, 9));
            return;
          } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
            e.preventDefault();
            setSubmenuIndex(prev => Math.max(prev - 1, 0));
            return;
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const apps = [
              { path: '/app/estimates' },
              { path: '/app/easy-estimate' },
              { path: '/app/notes' },
              { path: '/app/materials_approvals' },
              { path: '/app/schedules' },
              { path: '/app/maps' },
              { path: '/app/hr' },
              { path: '/app/contracts' },
              { path: '/app/tasker' },
              { path: '/app/requester' },
            ];
            const app = apps[submenuIndex];
            if (app) {
              navigate(app.path);
              closeSubmenu();
              setTimeout(() => onClose(), 0);
            }
            return;
          }
        }
        // Pozostałe klawisze (↑/↓/Enter) obsłuży wewnętrzny MenuList MUI dla aplikacji
        return;
      }

      if (!submenuOpen && (e.key === 'ArrowDown' || e.key === 'PageDown')) {
        e.preventDefault();
        setHighlighted(prev => (prev + 1) % items.length);
      } else if (!submenuOpen && (e.key === 'ArrowUp' || e.key === 'PageUp')) {
        e.preventDefault();
        setHighlighted(prev => (prev - 1 + items.length) % items.length);
      } else if (!submenuOpen && e.key === 'Home') {
        e.preventDefault();
        setHighlighted(0);
      } else if (!submenuOpen && e.key === 'End') {
        e.preventDefault();
        setHighlighted(Math.max(0, items.length - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = items[highlighted];
        if (item?.key === 'projects' || item?.key === 'apps' || item?.key === 'offers') {
          setSubmenuOpen(true);
          setSubmenuFor(item.key as 'projects' | 'apps' | 'offers');
          setSubmenuAnchorEl(document.getElementById(`overlay-${item.key}-item`));
          setSubmenuIndex(0);
          setProjectsExpanded(false);
          setProjectsListAnchorEl(null);
          setProjectsTreeIndex(0);
          setOffersExpanded(false);
          setOffersListAnchorEl(null);
          setOffersTreeIndex(0);
          if (item.key === 'projects' && projectCounts.inProgress === 0 && projectCounts.warranty === 0 && projectCounts.afterWarranty === 0 && !loadingProjects) loadProjects();
          if (item.key === 'offers' && offerCounts.offered === 0 && offerCounts.prequalification === 0 && offerCounts.finished === 0 && offerCounts.archive === 0 && !loadingOffers) loadOffers();
          return;
        }
        if (item) {
          navigate(item.path);
          closeSubmenu();
          setTimeout(() => onClose(), 0);
        }
      } else if (e.key === 'ArrowRight') {
        // Rozwiń submenu projektów
        const item = items[highlighted];
        if (item?.key === 'projects' || item?.key === 'apps' || item?.key === 'offers') {
          e.preventDefault();
          setSubmenuOpen(true);
          setSubmenuFor(item.key as 'projects' | 'apps' | 'offers');
          setSubmenuAnchorEl(document.getElementById(`overlay-${item.key}-item`));
          setSubmenuIndex(0);
          setProjectsExpanded(false);
          setProjectsListAnchorEl(null);
          setProjectsTreeIndex(0);
          setOffersExpanded(false);
          setOffersListAnchorEl(null);
          setOffersTreeIndex(0);
          if (item.key === 'projects' && projectCounts.inProgress === 0 && projectCounts.warranty === 0 && projectCounts.afterWarranty === 0 && !loadingProjects) loadProjects();
          if (item.key === 'offers' && offerCounts.offered === 0 && offerCounts.prequalification === 0 && offerCounts.finished === 0 && offerCounts.archive === 0 && !loadingOffers) loadOffers();
          return;
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
  };

  // Ustaw focus na własnym kontenerze
  React.useEffect(() => {
    if (open) setTimeout(() => containerRef.current?.focus(), 0);
  }, [open]);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: 257,
          bgcolor: '#fff',
          boxShadow: '0px 5px 30px rgba(0,0,0,0.05)',
          borderRight: 'none',
        }
      }}
    >
      <Box ref={containerRef} tabIndex={0} role="menu" onKeyDown={(e)=>handleKeys(e as any)} sx={{ outline: 'none' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 2 }}>
        <Typography
          component="span"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: '21px',
            color: '#c53030',
            letterSpacing: '0.12em',
            textTransform: 'uppercase'
          }}
        >
          UNITRESCO
        </Typography>
        <IconButton onClick={onClose} aria-label="Zamknij">
          <Close />
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ py: 1, position: 'relative' }}>
        {items.map((m, idx) => {
          const isHighlighted = idx === highlighted;
          const selected = isHighlighted; // podświetlamy tylko element w fokusie
          const isProjects = m.key === 'projects';
          const isApps = m.key === 'apps';
          const isOffers = m.key === 'offers';
          const hasArrow = ['projects','offers','database','apps','invoices','permissions','help','service'].includes(m.key);
          return (
            <ListItemButton
              key={m.key}
              component={isProjects || isApps || isOffers ? 'div' : Link}
              {...(isProjects || isApps || isOffers ? {} as any : { to: m.path })}
              onClick={(e)=>{
                if (isProjects || isApps || isOffers) {
                  e.preventDefault();
                  setSubmenuOpen(true);
                  setSubmenuFor(isProjects ? 'projects' : isApps ? 'apps' : 'offers');
                  setSubmenuAnchorEl(e.currentTarget as HTMLElement);
                  setSubmenuIndex(0);
                  setProjectsExpanded(false);
                  setProjectsListAnchorEl(null);
                  setProjectsTreeIndex(0);
                  setOffersExpanded(false);
                  setOffersListAnchorEl(null);
                  setOffersTreeIndex(0);
                  if (isProjects && projectCounts.inProgress === 0 && projectCounts.warranty === 0 && projectCounts.afterWarranty === 0 && !loadingProjects) loadProjects();
                  if (isOffers && offerCounts.offered === 0 && offerCounts.prequalification === 0 && offerCounts.finished === 0 && offerCounts.archive === 0 && !loadingOffers) loadOffers();
                } else {
                  setSubmenuOpen(false);
                  setSubmenuAnchorEl(null);
                  setTimeout(() => onClose(), 0);
                }
              }}
              onMouseEnter={(e)=>{ if (isProjects || isApps || isOffers) { setSubmenuOpen(true); setSubmenuFor(isProjects ? 'projects' : isApps ? 'apps' : 'offers'); setSubmenuAnchorEl(e.currentTarget as HTMLElement); setProjectsExpanded(false); setProjectsListAnchorEl(null); setProjectsTreeIndex(0); setOffersExpanded(false); setOffersListAnchorEl(null); setOffersTreeIndex(0); if (isProjects && projectCounts.inProgress === 0 && projectCounts.warranty === 0 && projectCounts.afterWarranty === 0 && !loadingProjects) loadProjects(); if (isOffers && offerCounts.offered === 0 && offerCounts.prequalification === 0 && offerCounts.finished === 0 && offerCounts.archive === 0 && !loadingOffers) loadOffers(); } }}
              selected={selected}
              sx={{
                borderRadius: 1,
                mx: 1,
                my: 0.5,
                '&:hover': {
                  bgcolor: '#F5F5FF',
                },
                '&.Mui-selected': {
                  bgcolor: '#F5F5FF',
                },
              }}
              id={isProjects ? 'overlay-projects-item' : isApps ? 'overlay-apps-item' : isOffers ? 'overlay-offers-item' : undefined}
              role="menuitem"
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Box component="img" src={m.icon} alt="" />
              </ListItemIcon>
              <ListItemText 
                primary={m.text} 
                primaryTypographyProps={{ 
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  fontWeight: selected ? 500 : 400,
                  color: '#020242'
                }} 
              />
              {hasArrow && (
                <Box component="img" src="/assets/sidebar/arrow_right.svg" alt="arrow" />
              )}
            </ListItemButton>
          );
        })}

        {/* Submenu projektów / Aplikacje / Oferty */}
        <Popper open={submenuOpen} anchorEl={submenuAnchorEl} placement="right-start" style={{ zIndex: 1400 }}>
          <ClickAwayListener onClickAway={()=>{ setSubmenuOpen(false); setSubmenuAnchorEl(null); setSubmenuFor(null); setProjectsExpanded(false); setProjectsTreeIndex(0); setOffersExpanded(false); setOffersTreeIndex(0); setTimeout(() => containerRef.current?.focus(), 0); }}>
            <Paper elevation={3} sx={{ minWidth: 400, maxWidth: 800, width: 'auto', maxHeight: '80vh', overflow: 'auto' }}>
              {submenuFor === 'apps' ? (
                <>
                  <MenuList autoFocusItem>
                    {[
                      { path: '/app/estimates', label: 'Kosztorysy', iconSrc: '/assets/icons/request_quote_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg' },
                      { path: '/app/easy-estimate', label: 'EasyEstimate', iconSrc: '/assets/icons/price_check.svg' },
                      { path: '/app/notes', label: 'Notatki', iconSrc: '/assets/icons/notes_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg' },
                      { path: '/app/materials_approvals', label: 'Karty materiałowe', iconSrc: '/assets/icons/description_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg' },
                      { path: '/app/schedules', label: 'Harmonogamy', iconSrc: '/assets/icons/schedule_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg' },
                      { path: '/app/maps', label: 'Logistyka', iconSrc: '/assets/icons/map_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg' },
                      { path: '/app/hr', label: 'HR', iconSrc: '/assets/icons/diversity_3_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg' },
                      { path: '/app/contracts', label: 'Umowy', iconSrc: '/assets/icons/contract_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg' },
                      { path: '/app/tasker', label: 'Tasker', iconSrc: '/assets/icons/tasker.svg' },
                      { path: '/app/requester', label: 'Requester', iconSrc: '/assets/icons/hand_gesture_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg' },
                    ].map((a, i) => (
                      <MenuItem 
                        key={a.path} 
                        role="menuitem" 
                        selected={i === submenuIndex} 
                        onMouseEnter={()=>setSubmenuIndex(i)} 
                        onClick={(e) => {
                          e.stopPropagation();
                          setSubmenuOpen(false); 
                          setSubmenuAnchorEl(null); 
                          setSubmenuFor(null); 
                          navigate(a.path); 
                          setTimeout(() => onClose(), 0);
                        }} 
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if(e.key==='ArrowLeft'){ 
                            e.preventDefault(); 
                            setSubmenuOpen(false); 
                            setSubmenuAnchorEl(null); 
                            setSubmenuFor(null); 
                            setTimeout(()=>containerRef.current?.focus(),0); 
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            navigate(a.path);
                            setSubmenuOpen(false);
                            setSubmenuAnchorEl(null);
                            setSubmenuFor(null);
                            setTimeout(() => onClose(), 0);
                          } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
                            e.preventDefault();
                            setSubmenuIndex(prev => Math.min(prev + 1, 9));
                          } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                            e.preventDefault();
                            setSubmenuIndex(prev => Math.max(prev - 1, 0));
                          }
                        }}
                        sx={{
                          '&:hover': { bgcolor: '#F5F5FF' },
                          '&.Mui-selected': { bgcolor: '#F5F5FF' },
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: '16px',
                          fontWeight: i === submenuIndex ? 500 : 400,
                          color: '#020242',
                          whiteSpace: 'nowrap',
                          overflow: 'visible',
                          textOverflow: 'clip',
                          pl: 2,
                          py: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                          <Box
                            component="img"
                            src={a.iconSrc}
                            alt=""
                            sx={{
                              width: 20,
                              height: 20,
                              flexShrink: 0
                            }}
                            onError={(e: any) => {
                              // Fallback do domyślnej ikony jeśli nie ma pliku
                              e.target.src = '/assets/icons/folder.svg';
                            }}
                          />
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', fontWeight: i === submenuIndex ? 500 : 400, color: '#020242' }}>
                            {a.label}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </MenuList>
                </>
              ) : submenuFor === 'offers' ? (
                <>
                  <MenuList autoFocusItem={false} disableListWrap>
                    {/* Aktualnie ofertowane - rozwijane */}
                    <MenuItem
                      role="menuitem"
                      selected={offersTreeIndex === 0 && !offersExpanded}
                      onMouseEnter={() => { 
                        if (!offersExpanded) {
                          setOffersTreeIndex(0);
                        }
                      }}
                      onClick={() => {
                        if (!offersExpanded) {
                          setOffersExpanded(true);
                          setOffersListAnchorEl(document.getElementById('offers-current-item'));
                          setSubmenuIndex(0);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowLeft' && offersExpanded) {
                          e.preventDefault();
                          setOffersExpanded(false);
                          setOffersListAnchorEl(null);
                          setOffersTreeIndex(0);
                          setSubmenuIndex(0);
                        } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
                          if (!offersExpanded) {
                            e.preventDefault();
                            const anchorEl = document.getElementById('offers-current-item');
                            setOffersExpanded(true);
                            setOffersListAnchorEl(anchorEl);
                            setSubmenuIndex(0);
                          }
                        } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
                          e.preventDefault();
                          if (!offersExpanded) {
                            setOffersTreeIndex(1);
                          }
                        } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                          e.preventDefault();
                          if (!offersExpanded && offersTreeIndex > 0) {
                            setOffersTreeIndex(prev => prev - 1);
                          }
                        }
                      }}
                      sx={{ 
                        pl: 2,
                        '&:hover': { bgcolor: '#F5F5FF' },
                        '&.Mui-selected': { bgcolor: '#F5F5FF' },
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '16px',
                        fontWeight: (offersTreeIndex === 0 && !offersExpanded) ? 500 : 400,
                        color: '#020242',
                        whiteSpace: 'nowrap',
                        overflow: 'visible',
                        textOverflow: 'clip'
                      }}
                      id="offers-current-item"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}>
                        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', fontWeight: (offersTreeIndex === 0 && !offersExpanded) ? 500 : 400, color: '#020242', overflow: 'visible', textOverflow: 'clip' }}>Aktualnie ofertowane ({offerCounts.offered})</Typography>
                        <Box component="img" src="/assets/sidebar/arrow_right.svg" alt="arrow" sx={{ flexShrink: 0, ml: 1 }} />
                      </Box>
                    </MenuItem>
                    
                    {/* Prekwalifikacja */}
                    <MenuItem
                      role="menuitem"
                      selected={offersTreeIndex === 1}
                      onMouseEnter={() => { if (!offersExpanded) setOffersTreeIndex(1); }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/offers?tab=1');
                        setSubmenuOpen(false);
                        setSubmenuAnchorEl(null);
                        setSubmenuFor(null);
                        setOffersExpanded(false);
                        setTimeout(() => onClose(), 0);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          navigate('/offers?tab=1');
                          setSubmenuOpen(false);
                          setSubmenuAnchorEl(null);
                          setSubmenuFor(null);
                          setOffersExpanded(false);
                          setTimeout(() => onClose(), 0);
                        } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
                          e.preventDefault();
                          if (!offersExpanded) {
                            setOffersTreeIndex(2);
                          }
                        } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                          e.preventDefault();
                          if (!offersExpanded) {
                            setOffersTreeIndex(0);
                          }
                        }
                      }}
                      sx={{ 
                        pl: 2,
                        '&:hover': { bgcolor: '#F5F5FF' },
                        '&.Mui-selected': { bgcolor: '#F5F5FF' },
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '16px',
                        fontWeight: offersTreeIndex === 1 ? 500 : 400,
                        color: '#020242',
                        whiteSpace: 'nowrap',
                        overflow: 'visible',
                        textOverflow: 'clip'
                      }}
                    >
                      Prekwalifikacja ({offerCounts.prequalification})
                    </MenuItem>
                    
                    {/* Zakończone */}
                    <MenuItem
                      role="menuitem"
                      selected={offersTreeIndex === 2}
                      onMouseEnter={() => { if (!offersExpanded) setOffersTreeIndex(2); }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/offers?tab=2');
                        setSubmenuOpen(false);
                        setSubmenuAnchorEl(null);
                        setSubmenuFor(null);
                        setOffersExpanded(false);
                        setTimeout(() => onClose(), 0);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          navigate('/offers?tab=2');
                          setSubmenuOpen(false);
                          setSubmenuAnchorEl(null);
                          setSubmenuFor(null);
                          setOffersExpanded(false);
                          setTimeout(() => onClose(), 0);
                        } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
                          e.preventDefault();
                          if (!offersExpanded) {
                            setOffersTreeIndex(3);
                          }
                        } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                          e.preventDefault();
                          if (!offersExpanded) {
                            setOffersTreeIndex(1);
                          }
                        }
                      }}
                      sx={{ 
                        pl: 2,
                        '&:hover': { bgcolor: '#F5F5FF' },
                        '&.Mui-selected': { bgcolor: '#F5F5FF' },
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '16px',
                        fontWeight: offersTreeIndex === 2 ? 500 : 400,
                        color: '#020242',
                        whiteSpace: 'nowrap',
                        overflow: 'visible',
                        textOverflow: 'clip'
                      }}
                    >
                      Zakończone ({offerCounts.finished})
                    </MenuItem>
                    
                    {/* Archiwum prekwalifikowanych */}
                    <MenuItem
                      role="menuitem"
                      selected={offersTreeIndex === 3}
                      onMouseEnter={() => { if (!offersExpanded) setOffersTreeIndex(3); }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/offers?tab=3');
                        setSubmenuOpen(false);
                        setSubmenuAnchorEl(null);
                        setSubmenuFor(null);
                        setOffersExpanded(false);
                        setTimeout(() => onClose(), 0);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          navigate('/offers?tab=3');
                          setSubmenuOpen(false);
                          setSubmenuAnchorEl(null);
                          setSubmenuFor(null);
                          setOffersExpanded(false);
                          setTimeout(() => onClose(), 0);
                        } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                          e.preventDefault();
                          if (!offersExpanded) {
                            setOffersTreeIndex(2);
                          }
                        }
                      }}
                      sx={{ 
                        pl: 2,
                        '&:hover': { bgcolor: '#F5F5FF' },
                        '&.Mui-selected': { bgcolor: '#F5F5FF' },
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '16px',
                        fontWeight: offersTreeIndex === 3 ? 500 : 400,
                        color: '#020242',
                        whiteSpace: 'nowrap',
                        overflow: 'visible',
                        textOverflow: 'clip'
                      }}
                    >
                      Archiwum prekwalifikowanych ({offerCounts.archive})
                    </MenuItem>
                  </MenuList>
                </>
              ) : (
                <>
                  <MenuList autoFocusItem={false} disableListWrap>
                    {/* W realizacji - rozwijane */}
                    <MenuItem
                      role="menuitem"
                      selected={projectsTreeIndex === 0 && !projectsExpanded}
                      onMouseEnter={() => { 
                        if (!projectsExpanded) {
                          setProjectsTreeIndex(0);
                        }
                      }}
                      onClick={() => {
                        if (!projectsExpanded) {
                          setProjectsExpanded(true);
                          setProjectsListAnchorEl(document.getElementById('projects-in-progress-item'));
                          setSubmenuIndex(0);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowLeft' && projectsExpanded) {
                          e.preventDefault();
                          setProjectsExpanded(false);
                          setProjectsListAnchorEl(null);
                          setProjectsTreeIndex(0);
                          setSubmenuIndex(0);
                        } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
                          if (!projectsExpanded) {
                            e.preventDefault();
                            const anchorEl = document.getElementById('projects-in-progress-item');
                            setProjectsExpanded(true);
                            setProjectsListAnchorEl(anchorEl);
                            setSubmenuIndex(0);
                          }
                        } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
                          e.preventDefault();
                          if (!projectsExpanded) {
                            setProjectsTreeIndex(1);
                          }
                        } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                          e.preventDefault();
                          if (!projectsExpanded && projectsTreeIndex > 0) {
                            setProjectsTreeIndex(prev => prev - 1);
                          }
                        }
                      }}
                      sx={{ 
                        pl: 2,
                        '&:hover': { bgcolor: '#F5F5FF' },
                        '&.Mui-selected': { bgcolor: '#F5F5FF' },
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '16px',
                        fontWeight: (projectsTreeIndex === 0 && !projectsExpanded) ? 500 : 400,
                        color: '#020242',
                        whiteSpace: 'nowrap',
                        overflow: 'visible',
                        textOverflow: 'clip'
                      }}
                      id="projects-in-progress-item"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}>
                        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', fontWeight: (projectsTreeIndex === 0 && !projectsExpanded) ? 500 : 400, color: '#020242', overflow: 'visible', textOverflow: 'clip' }}>W realizacji ({projectCounts.inProgress})</Typography>
                        <Box component="img" src="/assets/sidebar/arrow_right.svg" alt="arrow" sx={{ flexShrink: 0, ml: 1 }} />
                      </Box>
                    </MenuItem>
                    
                    {/* Zakończone w okresie gwarancji */}
                    <MenuItem
                      role="menuitem"
                      selected={projectsTreeIndex === 1}
                      onMouseEnter={() => { if (!projectsExpanded) setProjectsTreeIndex(1); }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/projects?tab=1');
                        setSubmenuOpen(false);
                        setSubmenuAnchorEl(null);
                        setSubmenuFor(null);
                        setProjectsExpanded(false);
                        setTimeout(() => onClose(), 0);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          navigate('/projects?tab=1');
                          setSubmenuOpen(false);
                          setSubmenuAnchorEl(null);
                          setSubmenuFor(null);
                          setProjectsExpanded(false);
                          setTimeout(() => onClose(), 0);
                        } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
                          e.preventDefault();
                          if (!projectsExpanded) {
                            setProjectsTreeIndex(2);
                          }
                        } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                          e.preventDefault();
                          if (!projectsExpanded) {
                            setProjectsTreeIndex(0);
                          }
                        }
                      }}
                      sx={{ 
                        pl: 2,
                        '&:hover': { bgcolor: '#F5F5FF' },
                        '&.Mui-selected': { bgcolor: '#F5F5FF' },
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '16px',
                        fontWeight: projectsTreeIndex === 1 ? 500 : 400,
                        color: '#020242',
                        whiteSpace: 'nowrap',
                        overflow: 'visible',
                        textOverflow: 'clip'
                      }}
                    >
                      Zakończone w okresie gwarancji ({projectCounts.warranty})
                    </MenuItem>
                    
                    {/* Zakończone po gwarancji */}
                    <MenuItem
                      role="menuitem"
                      selected={projectsTreeIndex === 2}
                      onMouseEnter={() => { if (!projectsExpanded) setProjectsTreeIndex(2); }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/projects?tab=2');
                        setSubmenuOpen(false);
                        setSubmenuAnchorEl(null);
                        setSubmenuFor(null);
                        setProjectsExpanded(false);
                        setTimeout(() => onClose(), 0);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          navigate('/projects?tab=2');
                          setSubmenuOpen(false);
                          setSubmenuAnchorEl(null);
                          setSubmenuFor(null);
                          setProjectsExpanded(false);
                          setTimeout(() => onClose(), 0);
                        } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                          e.preventDefault();
                          if (!projectsExpanded) {
                            setProjectsTreeIndex(1);
                          }
                        }
                      }}
                      sx={{ 
                        pl: 2,
                        '&:hover': { bgcolor: '#F5F5FF' },
                        '&.Mui-selected': { bgcolor: '#F5F5FF' },
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '16px',
                        fontWeight: projectsTreeIndex === 2 ? 500 : 400,
                        color: '#020242',
                        whiteSpace: 'nowrap',
                        overflow: 'visible',
                        textOverflow: 'clip'
                      }}
                    >
                      Zakończone po gwarancji ({projectCounts.afterWarranty})
                    </MenuItem>
                  </MenuList>
                </>
              )}
            </Paper>
          </ClickAwayListener>
        </Popper>

        {/* Submenu listy ofert aktualnie ofertowanych - po prawej stronie */}
        <Popper open={offersExpanded && submenuFor === 'offers'} anchorEl={offersListAnchorEl} placement="right-start" style={{ zIndex: 1500 }}>
          <ClickAwayListener onClickAway={()=>{ setOffersExpanded(false); setOffersListAnchorEl(null); setTimeout(() => containerRef.current?.focus(), 0); }}>
            <Paper elevation={3} sx={{ minWidth: 400, maxWidth: 800, width: 'auto', maxHeight: '80vh', overflow: 'auto' }}>
              <MenuList autoFocusItem={false} disableListWrap>
                {loadingOffers && (
                  <MenuItem disabled>Ładowanie…</MenuItem>
                )}
                {!loadingOffers && currentOffers.map((o, i) => (
                  <MenuItem 
                    key={o.id} 
                    role="menuitem" 
                    selected={i === submenuIndex} 
                    onMouseEnter={()=>setSubmenuIndex(i)} 
                    onClick={()=>{ 
                      setSubmenuOpen(false); 
                      setSubmenuAnchorEl(null); 
                      setSubmenuFor(null);
                      setOffersExpanded(false);
                      setOffersListAnchorEl(null);
                      navigate(`/offers/${encodeURIComponent(o.id)}`); 
                      setTimeout(() => onClose(), 0); 
                    }} 
                    onKeyDown={(e)=>{ 
                      if(e.key==='ArrowLeft'){ 
                        e.preventDefault(); 
                        setOffersExpanded(false);
                        setOffersListAnchorEl(null);
                        setOffersTreeIndex(0);
                        setSubmenuIndex(0);
                      } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
                        e.preventDefault();
                        setSubmenuIndex(prev => Math.min(prev + 1, currentOffers.length - 1));
                      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                        e.preventDefault();
                        setSubmenuIndex(prev => Math.max(prev - 1, 0));
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        navigate(`/offers/${encodeURIComponent(o.id)}`);
                        setSubmenuOpen(false);
                        setSubmenuAnchorEl(null);
                        setSubmenuFor(null);
                        setOffersExpanded(false);
                        setOffersListAnchorEl(null);
                        setTimeout(() => onClose(), 0);
                      }
                    }}
                    sx={{
                      '&:hover': { bgcolor: '#F5F5FF' },
                      '&.Mui-selected': { bgcolor: '#F5F5FF' },
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '16px',
                      fontWeight: i === submenuIndex ? 500 : 400,
                      color: '#020242',
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      textOverflow: 'clip'
                    }}
                  >
                    {o.label}
                  </MenuItem>
                ))}
                {!loadingOffers && currentOffers.length === 0 && (
                  <MenuItem disabled>Brak ofert aktualnie ofertowanych</MenuItem>
                )}
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>

        {/* Submenu listy projektów w realizacji - po prawej stronie */}
        <Popper open={projectsExpanded && submenuFor === 'projects'} anchorEl={projectsListAnchorEl} placement="right-start" style={{ zIndex: 1500 }}>
          <ClickAwayListener onClickAway={()=>{ setProjectsExpanded(false); setProjectsListAnchorEl(null); setTimeout(() => containerRef.current?.focus(), 0); }}>
            <Paper elevation={3} sx={{ minWidth: 400, maxWidth: 800, width: 'auto', maxHeight: '80vh', overflow: 'auto' }}>
              <MenuList autoFocusItem={false} disableListWrap>
                {loadingProjects && (
                  <MenuItem disabled>Ładowanie…</MenuItem>
                )}
                {!loadingProjects && inProgressProjects.map((p, i) => (
                  <MenuItem 
                    key={p.id} 
                    role="menuitem" 
                    selected={i === submenuIndex} 
                    onMouseEnter={()=>setSubmenuIndex(i)} 
                    onClick={()=>{ 
                      setSubmenuOpen(false); 
                      setSubmenuAnchorEl(null); 
                      setSubmenuFor(null);
                      setProjectsExpanded(false);
                      setProjectsListAnchorEl(null);
                      navigate(`/projects/${encodeURIComponent(p.id)}`); 
                      setTimeout(() => onClose(), 0); 
                    }} 
                    onKeyDown={(e)=>{ 
                      if(e.key==='ArrowLeft'){ 
                        e.preventDefault(); 
                        setProjectsExpanded(false);
                        setProjectsListAnchorEl(null);
                        setProjectsTreeIndex(0);
                        setSubmenuIndex(0);
                      } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
                        e.preventDefault();
                        setSubmenuIndex(prev => Math.min(prev + 1, inProgressProjects.length - 1));
                      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                        e.preventDefault();
                        setSubmenuIndex(prev => Math.max(prev - 1, 0));
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        navigate(`/projects/${encodeURIComponent(p.id)}`);
                        setSubmenuOpen(false);
                        setSubmenuAnchorEl(null);
                        setSubmenuFor(null);
                        setProjectsExpanded(false);
                        setProjectsListAnchorEl(null);
                        setTimeout(() => onClose(), 0);
                      }
                    }}
                    sx={{
                      '&:hover': { bgcolor: '#F5F5FF' },
                      '&.Mui-selected': { bgcolor: '#F5F5FF' },
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '16px',
                      fontWeight: i === submenuIndex ? 500 : 400,
                      color: '#020242',
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      textOverflow: 'clip'
                    }}
                  >
                    {p.label}
                  </MenuItem>
                ))}
                {!loadingProjects && inProgressProjects.length === 0 && (
                  <MenuItem disabled>Brak projektów w realizacji</MenuItem>
                )}
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </List>
      </Box>
    </Drawer>
  );
};

export default OverlaySidebar;


